import type { Project, Tag } from '@/types';
import { IconButton } from '@astryxdesign/core/IconButton';
import { Selector } from '@astryxdesign/core/Selector';
import { Plus, X } from 'lucide-react';
import type { DraftActivity, DraftRow } from '../lib/draft';
import { newActivity, rowMinutes } from '../lib/draft';
import { formatDuration } from '../lib/format';
import ActivityRow from './activity-row';

interface ProjectCardProps {
  row: DraftRow;
  color: string;
  tags: Tag[];
  projects: Project[];
  usedProjectIds: string[];
  readOnly: boolean;
  onChange: (row: DraftRow) => void;
  onRemove: () => void;
  onCreateTag: (activityId: string, label: string) => void;
}

export default function ProjectCard({
  row,
  color,
  tags,
  projects,
  usedProjectIds,
  readOnly,
  onChange,
  onRemove,
  onCreateTag,
}: ProjectCardProps) {
  // The catalog is predefined in the backend; projects already used by
  // other rows stay visible but can't be picked twice.
  const projectOptions = projects.map((project) => ({
    value: project.id,
    label: project.name,
    disabled: project.id !== row.projectId && usedProjectIds.includes(project.id),
  }));

  const selectProject = (projectId: string) => {
    const project = projects.find((item) => item.id === projectId);
    if (project) {
      onChange({ ...row, projectId: project.id, projectName: project.name });
    }
  };

  const updateActivity = (activityId: string, patch: Partial<DraftActivity>) =>
    onChange({
      ...row,
      activities: row.activities.map((activity) =>
        activity.id === activityId ? { ...activity, ...patch } : activity
      ),
    });

  return (
    <div
      className="iorio-reports-project mb-5"
      style={{ boxShadow: `6px 6px 0 color-mix(in srgb, ${color} 18%, transparent)` }}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-2.5">
          <span
            className="size-2.75 flex-none rounded-full"
            style={{ backgroundColor: color }}
            aria-hidden
          />
          {readOnly ? (
            <span className="text-primary truncate px-2 py-1 text-base font-bold">
              {row.projectName || 'Sin proyecto'}
            </span>
          ) : (
            <Selector
              label="Proyecto"
              isLabelHidden
              options={projectOptions}
              value={row.projectId ?? undefined}
              onChange={selectProject}
              placeholder="Elegí un proyecto"
              size="sm"
            />
          )}
        </div>
        <div className="flex flex-none items-center gap-2">
          <span
            title="Total del proyecto (automático)"
            className="bg-blue-subtle text-blue-vivid rounded-lg px-3 py-1.5 text-[13px] font-extrabold whitespace-nowrap"
          >
            {formatDuration(rowMinutes(row))}
          </span>
          {!readOnly && (
            <IconButton
              label="Quitar proyecto"
              tooltip="Quitar proyecto"
              icon={<X size={16} />}
              variant="ghost"
              onClick={onRemove}
            />
          )}
        </div>
      </div>

      <div className="iorio-reports-activities">
        {row.activities.map((activity) => (
          <ActivityRow
            key={activity.id}
            activity={activity}
            tags={tags}
            readOnly={readOnly}
            onChange={(patch) => updateActivity(activity.id, patch)}
            onRemove={() =>
              onChange({
                ...row,
                activities: row.activities.filter((item) => item.id !== activity.id),
              })
            }
            onCreateTag={(label) => onCreateTag(activity.id, label)}
          />
        ))}
        {!readOnly && (
          <button
            type="button"
            onClick={() => onChange({ ...row, activities: [...row.activities, newActivity()] })}
            className="iorio-reports-add-activity"
          >
            <Plus size={14} strokeWidth={2.6} />
            Actividad
          </button>
        )}
      </div>
    </div>
  );
}
